import React, { useState, useEffect } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { ChevronRight, Star, ShoppingCart, Minus, Plus, ShieldCheck, Truck, RotateCcw, Check, Loader2, Scale, Zap } from "lucide-react";
import api from "../services/api";
import ProductCard, { ProductCardSkeleton } from "../components/ProductCard";
import { useCart } from "../context/CartContext";
import { useSettings } from "../context/SettingsContext";
import { useCompare } from "../context/CompareContext";
import { RecentlyViewed, AlsoBought, recordView } from "../components/Recommendations";
import { Gallery } from "./product/Gallery";
import { VehicleCompat } from "./product/VehicleCompat";
import { ProductTabs } from "./product/ProductTabs";
import { Reveal } from "../components/fx/Reveal";
import { AvailabilityBadge } from "../components/AvailabilityBadge";

const eur = (n) => `${Number(n).toFixed(2).replace(".", ",")} €`;

const ProductPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { addItem } = useCart();
  const settings = useSettings();
  const compare = useCompare();
  const [product, setProduct] = useState(null);
  const [related, setRelated] = useState([]);
  const [loading, setLoading] = useState(true);
  const [qty, setQty] = useState(1);
  const [added, setAdded] = useState(false);

  useEffect(() => {
    setLoading(true); setQty(1);
    api.get(`/products/${id}`)
      .then(({ data }) => { setProduct(data); recordView(data.id); return api.get("/products", { params: { category: data.category } }); })
      .then((res) => { if (res) setRelated(res.data.filter((p) => p.id !== id).slice(0, 4)); })
      .catch(() => setProduct(null))
      .finally(() => setLoading(false));
  }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  if (loading) {
    return (
      <div className="max-w-[1440px] mx-auto px-5 py-8" data-testid="product-loading">
        <div className="h-4 w-64 rounded shimmer mb-6" />
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10"><div className="h-[360px] md:h-[480px] rounded-2xl shimmer" /><div className="space-y-4"><div className="h-3 w-20 rounded shimmer" /><div className="h-9 w-3/4 rounded shimmer" /><div className="h-4 w-40 rounded shimmer" /><div className="h-12 w-48 rounded shimmer mt-6" /><div className="h-14 w-full rounded-xl shimmer mt-8" /></div></div>
      </div>
    );
  }
  if (!product) {
    return (
      <div className="max-w-[1440px] mx-auto px-5 py-20 text-center">
        <p className="text-slate-400 mb-4">Produit introuvable.</p>
        <Link to="/" className="text-[#FF3B3B] font-semibold hover:underline">Retour à l'accueil</Link>
      </div>
    );
  }

  const discount = product.old_price ? Math.round(((product.old_price - product.price) / product.old_price) * 100) : 0;
  const avail = product.availability;
  const out = avail ? !avail.available : product.stock <= 0;
  const low = !avail && !out && product.stock <= 5;
  const handleAdd = () => { if (out) return; addItem(product, qty); setAdded(true); setTimeout(() => setAdded(false), 1800); };

  return (
    <div className="max-w-[1440px] mx-auto px-5 py-8 pb-28 lg:pb-8" data-testid="product-page">
      <div className="flex items-center gap-1.5 text-[14px] text-slate-400 mb-6 flex-wrap">
        <Link to="/" className="hover:text-[#FF3B3B]">Accueil</Link><ChevronRight size={15} />
        <Link to={`/categorie/${product.category}`} className="hover:text-[#FF3B3B] capitalize">{product.category.replace("-", " ")}</Link><ChevronRight size={15} />
        <span className="text-slate-50 font-semibold truncate max-w-[60vw]">{product.name}</span>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
        <Gallery product={product} />
        <div>
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-[13px] font-bold text-[#FF3B3B] uppercase tracking-wide">{product.brand}</span>
            {discount > 0 && <span className="badge-promo text-[11px] font-black italic px-2 py-0.5 rounded-md">PROMO -{discount}%</span>}
          </div>
          <h1 className="font-extrabold italic uppercase tracking-tight text-slate-50 text-3xl md:text-4xl leading-tight mt-1" data-testid="product-title">{product.name}</h1>
          <div className="flex items-center gap-3 mt-3">
            <div className="flex items-center gap-1">{[...Array(5)].map((_, i) => <Star key={i} size={17} className={i < Math.round(product.rating) ? "fill-[#f5a623] text-[#f5a623]" : "text-slate-600"} />)}</div>
            <span className="text-[14px] text-slate-400" data-testid="product-rating-summary">{product.reviews > 0 ? `${product.rating} · ${product.reviews} avis` : "Aucun avis pour le moment"}</span>
            <span className="text-slate-600">·</span>
            <span className="text-[14px] text-slate-400">Réf. <span className="font-mono font-semibold text-slate-50">{product.ref}</span></span>
          </div>

          <div className="flex items-end gap-3 mt-6">
            <span className="text-4xl md:text-5xl font-black text-slate-50 leading-none" data-testid="product-price">{eur(product.price)}</span>
            {product.old_price && <span className="text-xl text-slate-500 line-through mb-1">{eur(product.old_price)}</span>}
            {discount > 0 && <span className="text-emerald-400 font-bold text-sm mb-1">Économisez {eur(product.old_price - product.price)}</span>}
          </div>
          <div className="text-[12px] text-slate-500 mt-1">TTC · TVA incluse</div>
          {avail ? <AvailabilityBadge availability={avail} testid="product-availability" /> : (
          <div data-testid="product-stock" className={`flex items-center gap-2 mt-3 text-[14px] font-semibold ${out ? "text-red-400" : low ? "text-amber-300" : "text-emerald-400"}`}>
            {out ? <>Rupture de stock — réapprovisionnement en cours</> : low ? <><Zap size={16} /> Plus que {product.stock} en stock — commandez vite</> : <><Check size={16} /> En stock ({product.stock} disponibles) · Expédié en {settings.delivery_delay}</>}
          </div>)}

          {!["pneumatiques", "performance"].includes(product.category) && <VehicleCompat product={product} />}

          <p className="text-slate-300 leading-relaxed mt-5 line-clamp-4">{product.desc}</p>

          <div className="flex items-center gap-4 mt-7">
            <div className="flex items-center border-2 border-[#222735] rounded-xl">
              <button data-testid="product-qty-minus" onClick={() => setQty(Math.max(1, qty - 1))} className="px-3 py-3 text-slate-50 hover:text-[#FF3B3B]"><Minus size={18} /></button>
              <span data-testid="product-qty" className="w-10 text-center font-bold text-slate-50">{qty}</span>
              <button data-testid="product-qty-plus" onClick={() => setQty(qty + 1)} className="px-3 py-3 text-slate-50 hover:text-[#FF3B3B]"><Plus size={18} /></button>
            </div>
            <button data-testid="product-add-to-cart" onClick={handleAdd} disabled={out} className={`flex-1 rounded-xl py-3.5 font-bold text-lg flex items-center justify-center gap-2 active:scale-[0.98] transition-all disabled:opacity-40 ${added ? "bg-emerald-600 text-white" : "btn-orange btn-shine"}`}>
              {added ? <><Check size={20} /> Ajouté au panier</> : <><ShoppingCart size={20} /> Ajouter au panier</>}
            </button>
          </div>
          <button data-testid="product-buy-now" onClick={() => { if (out) return; addItem(product, qty); navigate("/panier"); }} disabled={out} className="mt-3 w-full rounded-xl py-3.5 font-bold text-lg border-2 border-slate-500 text-slate-50 hover:bg-[#C81818] hover:border-[#C81818] hover:text-white transition-colors disabled:opacity-40">
            Commander maintenant
          </button>
          <button data-testid="product-page-compare" onClick={() => compare.toggle(product.id)} aria-pressed={compare.has(product.id)} className={`mt-3 w-full rounded-xl py-2.5 font-semibold text-[14px] border-2 flex items-center justify-center gap-2 transition-colors ${compare.has(product.id) ? "border-[#E01F1F] text-[#FF3B3B] bg-[#E01F1F]/10" : "border-[#222735] text-slate-300 hover:border-slate-500"}`}>
            <Scale size={16} /> {compare.has(product.id) ? "Dans le comparateur — retirer" : `Comparer cette pièce (${compare.ids.length}/${compare.max})`}
          </button>

          <div className="grid grid-cols-3 gap-3 mt-7">
            {[{ i: Truck, t: avail?.delivery_date && avail.available ? `Livraison estimée le ${new Date(avail.delivery_date).toLocaleDateString("fr-FR", { day: "numeric", month: "short" })}` : avail && !avail.available ? "Produit arrêté" : `Livré en ${settings.delivery_delay}` }, { i: ShieldCheck, t: "Garantie 2 ans" }, { i: RotateCcw, t: `Retour ${settings.return_days} jours` }].map((x, k) => (
              <div key={k} className="flex flex-col items-center text-center gap-1.5 bg-[#181C26] rounded-xl py-4 px-2 border border-transparent hover:border-[#2A3040] transition-colors">
                <x.i size={22} className="text-slate-50" strokeWidth={1.7} />
                <span className="text-[12px] font-semibold text-slate-300">{x.t}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      <ProductTabs product={product} settings={settings} onProductChange={() => api.get(`/products/${id}`).then(({ data }) => setProduct(data)).catch(() => {})} />
      <AlsoBought productId={product.id} />

      {related.length > 0 && (
        <Reveal>
          <section className="mt-16" data-testid="product-related">
            <h2 className="font-extrabold italic uppercase tracking-tight text-slate-50 text-2xl md:text-3xl mb-6">Produits similaires</h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-5">{related.map((p) => <ProductCard key={p.id} product={p} />)}</div>
          </section>
        </Reveal>
      )}
      {loading && <div className="grid grid-cols-2 md:grid-cols-4 gap-5 mt-16">{[1, 2, 3, 4].map((i) => <ProductCardSkeleton key={i} />)}</div>}
      <RecentlyViewed exclude={product.id} />

      {/* Sticky mobile CTA */}
      <div data-testid="product-sticky-cta" className="lg:hidden fixed bottom-[60px] md:bottom-0 inset-x-0 z-30 bg-[#0D0F14]/95 backdrop-blur-md border-t border-[#1E232D] px-4 py-3 flex items-center gap-3">
        <div className="min-w-0">
          <div className="text-[11px] text-slate-400 truncate">{product.brand} · {product.ref}</div>
          <div className="font-extrabold text-white text-lg leading-tight">{eur(product.price * qty)}</div>
        </div>
        <button data-testid="product-sticky-add" onClick={handleAdd} disabled={out} className={`ml-auto rounded-xl px-5 py-3 font-extrabold text-[14px] flex items-center gap-2 active:scale-[0.98] transition-all disabled:opacity-40 ${added ? "bg-emerald-600 text-white" : "btn-orange"}`}>
          {added ? <><Check size={18} /> Ajouté</> : <><ShoppingCart size={18} /> {out ? "Indisponible" : "Ajouter"}</>}
        </button>
      </div>
    </div>
  );
};

export default ProductPage;
